import { isSMCOrg } from './isSMCOrg'
import { isTodayOrLater } from 'app/date.utils'
import { CONTACT_PHI_FIELDS } from './phiFields'
import { ContactJson } from 'app/types/entities'
import { ContactFieldRendererProps } from 'types/ContactDrawerSection'

/**
 * Single source of truth for all contact field configurations
 */
export interface FieldConfig {
  id: string
  label: string
  className?: string
  isDate?: boolean
  isPhone?: boolean
  isLocked?: boolean
  canCopy?: boolean
  isEditable?: boolean
  editFieldName?: string
  infoTooltip?: { title: string; message: string }
  conditionalRender?: (props: ContactFieldRendererProps) => boolean
  section: `spotlight` | `information` | `financial` | `dates` | `marketing`
  isCore?: boolean
  superUserOnly?: boolean
  isSMC?: boolean
  getValue: (contact: ContactJson, campaignName?: string) => any
}

function addPhiClass(className?: string, fieldId?: string): string | undefined {
  let isPhi = fieldId ? CONTACT_PHI_FIELDS.has(fieldId) : false
  if (!isPhi) return className
  return className ? `${className} phi` : `phi`
}

/**
 * Unified field configuration - single source of truth
 * PHI blurring is auto-derived from CONTACT_PHI_FIELDS (synced with backend GraphQL model metadata)
 */
export const CONTACT_FIELDS: FieldConfig[] = (
  [
    // Spotlight fields
    {
      id: `email`,
      label: `Email`,
      className: `lowercase`,
      canCopy: true,
      section: `information`,
      getValue: (contact) => contact.email,
    },
    {
      id: `cellPhone`,
      label: `Cell Phone`,
      isPhone: true,
      section: `information`,
      getValue: (contact) => contact.cellPhone,
    },
    {
      id: `lifecycle`,
      label: `Lifecycle`,
      infoTooltip: {
        title: `Lifecycle`,
        message: `The stage in the new patient journey the contact is in. This field is set by the Trackable's Algorithm based on data from a number of factors.`,
      },
      section: `spotlight`,
      isCore: true,
      getValue: (contact) =>
        contact.patientStatus === `NonPatient` && contact.lifecycleStage === `Patient` ? `` : contact.lifecycleStage,
      className: `!text-green-600`,
    },
    {
      id: `leadStatus`,
      label: `Lead Status`,
      isEditable: true,
      editFieldName: `leadStatus`,
      infoTooltip: {
        title: `Lead Status`,
        message: `The stage a contact is in during the lead lifecyle. This field has a definitive ending point at either closed won or closed lost. This field is set by the Trackable's Algorithm or manually by a user.`,
      },
      section: `spotlight`,
      isCore: true,
      getValue: (contact) => contact.leadStatus,
    },
    {
      id: `leadOwner`,
      label: `Contact Owner`,
      isEditable: true,
      editFieldName: `leadOwner`,
      infoTooltip: {
        title: `Contact Owner`,
        message: `The person in your practice who owns the relationship with the contact.`,
      },
      section: `spotlight`,
      isCore: true,
      getValue: (contact) => contact.leadOwner?.name || `—`,
    },
    {
      id: `qualification`,
      label: `Qualification`,
      infoTooltip: {
        title: `Qualification`,
        message: `Whether or not the contact is considered to be qualified based on your practice's standards.`,
      },
      section: `marketing`,
      getValue: (contact) => (contact.isQualified ? `Qualified` : contact.isQualified === false ? `Disqualified` : `—`),
    },
    {
      id: `source`,
      label: `Source`,
      isEditable: true,
      editFieldName: `utmSource`,
      conditionalRender: (props) => !isSMCOrg(props.contact.orgId),
      infoTooltip: {
        title: `Source`,
        message: `The source where the contact was first recognized. Common values are websites, referrals, and walk-ins.`,
      },
      section: `marketing`,
      isSMC: false,
      getValue: (contact) => contact.utmSource,
    },
    {
      id: `medium`,
      label: `Medium`,
      className: `lowercase`,
      isEditable: true,
      editFieldName: `utmMedium`,
      conditionalRender: (props) => !isSMCOrg(props.contact.orgId),
      infoTooltip: {
        title: `Medium`,
        message: `The medium that the contact used to find you. Examples include organic search, paid search, direct, and referral.`,
      },
      section: `marketing`,
      isSMC: false,
      getValue: (contact) => contact.utmMedium,
    },
    {
      id: `campaign`,
      label: `Campaign`,
      isEditable: true,
      editFieldName: `utmCampaign`,
      conditionalRender: (props) => !isSMCOrg(props.contact.orgId),
      infoTooltip: {
        title: `Campaign`,
        message: `The campaign that the contact came from. This field is automatically populated by various integrations.`,
      },
      section: `marketing`,
      isSMC: false,
      getValue: (contact) => contact.utmCampaign,
    },
    {
      id: `channel`,
      label: `Channel`,
      isEditable: true,
      editFieldName: `channel`,
      infoTooltip: {
        title: `Channel`,
        message: `The channel that the contact used to find you or be found by you. Examples include phone, email, chat, and in-person.`,
      },
      section: `marketing`,
      getValue: (contact) => contact.channel,
    },
    // Information fields
    {
      id: `patientStatus`,
      label: `Patient Status`,
      className: `capitalized`,
      section: `information`,
      getValue: (contact) => contact.patientStatus,
    },
    {
      id: `appointmentStatus`,
      label: `Appointment Status`,
      className: `capitalized`,
      infoTooltip: {
        title: `Appointment Status`,
        message: `The status of a contact's most recent or upcoming appointment. This field is set by the Trackable's Algorithm based on data from your connected PMS.`,
      },
      section: `information`,
      getValue: (contact) => contact.appointmentStatus,
    },
    {
      id: `birthDate`,
      label: `Date of Birth`,
      isDate: true,
      section: `information`,
      getValue: (contact) => contact.birthDate,
    },
    {
      id: `homePhone`,
      label: `Home Phone`,
      isPhone: true,
      section: `information`,
      getValue: (contact) => contact.homePhone,
    },
    {
      id: `workPhone`,
      label: `Work Phone`,
      isPhone: true,
      section: `information`,
      getValue: (contact) => contact.workPhone,
    },
    {
      id: `addressLine1`,
      label: `Address Line 1`,
      className: `capitalized`,
      section: `information`,
      getValue: (contact) => contact.addressLine1,
    },
    {
      id: `addressLine2`,
      label: `Address Line 2`,
      className: `capitalized`,
      section: `information`,
      getValue: (contact) => contact.addressLine2,
    },
    {
      id: `city`,
      label: `City`,
      className: `capitalized`,
      section: `information`,
      getValue: (contact) => contact.city,
    },
    {
      id: `zip`,
      label: `Zip code`,
      section: `information`,
      getValue: (contact) => contact.zip,
    },
    {
      id: `preferredCommunicationMethod`,
      label: `Preferred Contact Method`,
      className: `capitalized`,
      section: `information`,
      getValue: (contact) => contact.preferredCommunicationMethod,
    },
    {
      id: `preferredContact`,
      label: `Preferred Contact`,
      className: `capitalized`,
      section: `information`,
      getValue: (contact) => contact.preferredContact,
    },
    {
      id: `neededAction`,
      label: `Needed Action?`,
      conditionalRender: (props) => props.user?.type === `super`,
      section: `information`,
      superUserOnly: true,
      getValue: (contact) => (contact.neededAction ? `Yes` : `No`),
    },

    // Financial fields
    {
      id: `collected`,
      label: `Collected`,
      infoTooltip: { title: `Collected`, message: `Value of all payment transactions.` },
      section: `financial`,
      getValue: (contact) =>
        Number(contact.collectedLtv ?? 0).toLocaleString(`en-US`, {
          style: `currency`,
          currency: `USD`,
        }),
    },
    {
      id: `productionLtv`,
      label: `Net Production`,
      infoTooltip: {
        title: `Net Production`,
        message: `Value of gross production minus the value of adjustments.`,
      },
      section: `financial`,
      getValue: (contact) =>
        Number(contact.productionLtv ?? 0).toLocaleString(`en-US`, {
          style: `currency`,
          currency: `USD`,
        }),
    },
    {
      id: `scheduledProduction`,
      label: `Scheduled Production`,
      infoTooltip: {
        title: `Scheduled Production`,
        message: `Value of the appointments that are scheduled for the future.`,
      },
      section: `financial`,
      getValue: (contact) =>
        Number(contact.scheduledProduction ?? 0).toLocaleString(`en-US`, {
          style: `currency`,
          currency: `USD`,
        }),
    },

    // Date fields
    {
      id: `firstActivityDate`,
      label: `First Activity Date`,
      isDate: true,
      infoTooltip: {
        title: `First Activity Date`,
        message: `The date of the first activity on the contact.`,
      },
      section: `dates`,
      getValue: (contact) => contact.firstActivityDate,
    },
    {
      id: `lastActivityDate`,
      label: `Last Activity Date`,
      isDate: true,
      infoTooltip: {
        title: `Last Activity Date`,
        message: `The date of the last activity on the contact.`,
      },
      section: `dates`,
      getValue: (contact) => contact.lastActivityDate,
    },
    {
      id: `becameInactiveDate`,
      label: `Became Inactive Date`,
      isDate: true,
      infoTooltip: {
        title: `Became Inactive Date`,
        message: `The date the contact was last marked as inactive. Trackable considers a contact to be inactive after 18 months without an activity.`,
      },
      section: `dates`,
      getValue: (contact) => contact.becameInactiveDate,
    },
    {
      id: `becameLeadDate`,
      label: `Became a Lead Date`,
      isDate: true,
      infoTooltip: {
        title: `Became a Lead Date`,
        message: `The date the contact was recognized as a lead. This date is the earliest date between the following timestamps: (communications activity date, appointment made date, first transaction date)`,
      },
      section: `dates`,
      getValue: (contact) => contact.becameLeadDate,
    },
    {
      id: `firstAppointmentMadeDate`,
      label: `Became a New Patient Scheduled Date`,
      isDate: true,
      infoTooltip: {
        title: `Became a New Patient Scheduled Date`,
        message: `The date the contact was recognized as a New Patient Scheduled. This date is set from the first appointment made date.`,
      },
      section: `dates`,
      getValue: (contact) => contact.firstAppointmentMadeDate,
    },
    {
      id: `becamePatientDate`,
      label: `Became a Patient Date`,
      isDate: true,
      infoTooltip: {
        title: `Became a Patient Date`,
        message: `The date the contact is recognized as a Patient. This date is set by identifying the first completed transaction with an associated valid dental code.`,
      },
      section: `dates`,
      getValue: (contact) => contact.becamePatientDate,
    },
    {
      id: `firstAppointmentDate`,
      label: `First Appointment Date`,
      isDate: true,
      infoTooltip: {
        title: `First Appointment Date`,
        message: `The date of the first appointment the associated patient record has.`,
      },
      section: `dates`,
      getValue: (contact) => contact.firstAppointmentDate,
    },
    {
      id: `lastAppointmentDate`,
      label: `Last Appointment Date`,
      isDate: true,
      infoTooltip: {
        title: `Last Appointment Date`,
        message: `The date of the most recent appointment date in the past.`,
      },
      section: `dates`,
      getValue: (contact) => contact.lastAppointmentDate,
    },
    {
      id: `nearestAppointmentDate`,
      label: `Next Appointment Date`,
      isDate: true,
      infoTooltip: {
        title: `Next Appointment Date`,
        message: `The date of the next scheduled appointment.`,
      },
      section: `dates`,
      getValue: (contact) =>
        isTodayOrLater(contact.nearestAppointmentDate) ? contact.nearestAppointmentDate : undefined,
    },
    {
      id: `lastHygieneDate`,
      label: `Last Hygiene Date`,
      isDate: true,
      infoTooltip: {
        title: `Last Hygiene Date`,
        message: `The date of the patient's most recent hygiene procedure (prophy or perio).`,
      },
      section: `dates`,
      getValue: (contact) => contact.lastHygieneDate,
    },
    {
      id: `hygieneDueDate`,
      label: `Hygiene Due Date`,
      isDate: true,
      infoTooltip: {
        title: `Hygiene Due Date`,
        message: `The date when the patient is next due for hygiene, based on their last hygiene date plus recall interval.`,
      },
      section: `dates`,
      getValue: (contact) => contact.hygieneDueDate,
    },
    {
      id: `isPerio`,
      label: `Is Perio`,
      infoTooltip: {
        title: `Is Perio`,
        message: `Whether the patient is a periodontal patient. Once true, this does not revert.`,
      },
      section: `dates`,
      getValue: (contact) => (contact.isPerio ? `Yes` : undefined),
    },
    // Marketing fields
    {
      id: `closedLostReason`,
      label: `Closed Lost Reason`,
      infoTooltip: {
        title: `Closed Lost Reason`,
        message: `If a contact is marked as closed lost, the closed lost reason gives more details as to why they should be considered lost.`,
      },
      section: `marketing`,
      getValue: (contact) => contact.closedLostReason,
    },
    {
      id: `leadConversionStatus`,
      label: `Lead Conversion Status`,
      section: `marketing`,
      getValue: (contact) => contact.leadConversionStatus,
    },
    {
      id: `firstContactReason`,
      label: `First Contact Reason`,
      section: `marketing`,
      getValue: (contact) => contact.firstContactReason,
    },
    {
      id: `firstActivityType`,
      label: `First Activity Type`,
      section: `marketing`,
      getValue: (contact) => contact.firstActivityType,
    },
    {
      id: `content`,
      label: `Content`,
      section: `marketing`,
      getValue: (contact) => contact.utmContent,
    },
    {
      id: `GCLID`,
      label: `GCLID`,
      section: `marketing`,
      getValue: (contact) => contact.gclid,
    },
    {
      id: `FBCLID`,
      label: `FBCLID`,
      section: `marketing`,
      getValue: (contact) => contact.fbclid,
    },
    {
      id: `referralURL`,
      label: `Referral URL`,
      section: `marketing`,
      getValue: (contact) => contact.referralURL,
    },
    {
      id: `landingPageURL`,
      label: `Landing Page URL`,
      section: `marketing`,
      getValue: (contact) => contact.landingPageURL,
    },
    {
      id: `reportedAttribution`,
      label: `Reported Attribution`,
      isEditable: true,
      editFieldName: `reportedAttribution`,
      section: `marketing`,
      getValue: (contact) => contact.reportedAttribution,
    },
    {
      id: `isQualified`,
      label: `Qualification Status`,
      infoTooltip: {
        title: `Qualification`,
        message: `Whether or not the contact is considered to be qualified based on your practice's standards.`,
      },
      section: `marketing`,
      getValue: (contact) => (contact.isQualified ? `Qualified` : contact.isQualified === false ? `Disqualified` : `—`),
    },
    {
      id: `smsMarketingConsent`,
      label: `SMS Consent`,
      infoTooltip: {
        title: `SMS Consent`,
        message: `Computed SMS consent that gates sends. Opted in allows all SMS; Opted out blocks everything including transactional; Empty means no signal (transactional may send, marketing may not).`,
      },
      section: `marketing`,
      getValue: (contact) =>
        contact.smsMarketingConsent ? `Opted in` : contact.smsMarketingConsent === false ? `Opted out` : `—`,
    },
  ] satisfies FieldConfig[]
).map((field) => ({ ...field, className: addPhiClass(field.className, field.id) }))

/**
 * Get field configuration by ID
 */
export function getFieldConfig(fieldId: string): FieldConfig | undefined {
  return CONTACT_FIELDS.find((field) => field.id === fieldId)
}

/**
 * Get field value from contact data using unified configuration
 */
export function getFieldValue(fieldId: string, contact: ContactJson, campaignName?: string): any {
  const fieldConfig = getFieldConfig(fieldId)
  return fieldConfig ? fieldConfig.getValue(contact, campaignName) : ``
}

/**
 * Get all field IDs by section
 */
export function getFieldIdsBySection(section: FieldConfig[`section`]): string[] {
  return CONTACT_FIELDS.filter((field) => field.section === section).map((field) => field.id)
}
